import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
} from 'recharts';
import { Calendar, UserPlus } from 'lucide-react';
import TimeFilterControls, { TimeFilter } from './TimeFilterControls';

interface RegistrationData {
  date: string;
  registrations: number;
}

interface UserRegistrationChartData {
  timeline: RegistrationData[];
  total_registrations: number;
  period_start: string | null;
  period_end: string | null;
}

const UserRegistrationChart: React.FC = () => {
  const [data, setData] = useState<UserRegistrationChartData | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [timeFilter, setTimeFilter] = useState<TimeFilter>({});
  const previousFilterRef = useRef<string>('');

  const fetchRegistrationData = useCallback(async (filter: TimeFilter) => {
    try {
      setLoading(true);
      setError(null);

      const apiUrl = process.env.REACT_APP_API_URL || '';
      const token = localStorage.getItem('auth_token');
      const headers: HeadersInit = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      };

      const params = new URLSearchParams();
      if (filter.limitDays) {
        params.append('limit_days', filter.limitDays.toString());
      }
      if (filter.startDate) {
        params.append('start_date', filter.startDate);
      }
      if (filter.endDate) {
        params.append('end_date', filter.endDate);
      }

      const url = `${apiUrl}/api/admin/users/registration-timeline?${params.toString()}`;
      const response = await fetch(url, { headers });

      if (!response.ok) {
        throw new Error(`Failed to fetch registration data: ${response.status}`);
      }

      const registrationData = await response.json();
      setData(registrationData);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch registration data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const filterKey = JSON.stringify(timeFilter);
    if (filterKey !== previousFilterRef.current) {
      previousFilterRef.current = filterKey;
      fetchRegistrationData(timeFilter);
    }
  }, [timeFilter, fetchRegistrationData]);

  const handleFilterChange = useCallback((filter: TimeFilter) => {
    setTimeFilter(filter);
  }, []);

  if (loading) {
    return (
      <div className="h-80 bg-white rounded-lg border border-gray-200 p-6">
        <div className="flex items-center justify-center h-full">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
          <span className="ml-3 text-gray-600">Loading chart data...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-lg border border-red-200 p-6">
        <TimeFilterControls onFilterChange={handleFilterChange} defaultFilter={timeFilter} />
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-sm text-red-600">Error: {error}</p>
        </div>
      </div>
    );
  }

  if (!data || !data.timeline || data.timeline.length === 0) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <TimeFilterControls onFilterChange={handleFilterChange} defaultFilter={timeFilter} />
        <div className="flex flex-col items-center justify-center h-64 text-gray-500">
          <Calendar className="h-12 w-12 mb-4" />
          <p className="text-lg font-medium">No registration data available</p>
          <p className="text-sm">Check back once users start registering</p>
        </div>
      </div>
    );
  }

  const registrationData = data.timeline;

  // Process data for better visualization
  const processedData = registrationData.map(item => ({
    ...item,
    formattedDate: new Date(item.date).toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric' 
    }),
    fullDate: new Date(item.date).toLocaleDateString('en-US', { 
      weekday: 'long',
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    })
  }));

  const totalRegistrations = data.total_registrations;
  const periodTotal = registrationData.reduce((sum, item) => sum + item.registrations, 0);
  const maxRegistrations = Math.max(...registrationData.map(item => item.registrations), 0);
  const averageRegistrations = periodTotal / registrationData.length;

  // Custom tooltip
  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="bg-white p-3 border border-gray-200 rounded-lg shadow-lg">
          <p className="font-medium text-gray-900">{data.fullDate}</p>
          <p className="text-indigo-600">
            <span className="font-medium">{payload[0].value}</span> new registrations
          </p>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200">
      <TimeFilterControls onFilterChange={handleFilterChange} defaultFilter={timeFilter} />
      
      {/* Header with stats */}
      <div className="p-6 border-b border-gray-200">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center">
            <UserPlus className="h-6 w-6 text-indigo-600 mr-2" />
            <h3 className="text-lg font-semibold text-gray-900">
              User Registration Timeline
            </h3>
          </div>
        </div>
        
        {/* Summary stats */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="bg-indigo-50 rounded-lg p-3">
            <div className="text-sm font-medium text-indigo-600">Total Registrations (All Time)</div>
            <div className="text-2xl font-bold text-indigo-900">{totalRegistrations}</div>
          </div>
          <div className="bg-green-50 rounded-lg p-3">
            <div className="text-sm font-medium text-green-600">Peak Day</div>
            <div className="text-2xl font-bold text-green-900">{maxRegistrations}</div>
          </div>
          <div className="bg-amber-50 rounded-lg p-3">
            <div className="text-sm font-medium text-amber-600">Daily Average</div>
            <div className="text-2xl font-bold text-amber-900">{averageRegistrations.toFixed(1)}</div>
          </div>
        </div>
      </div>

      {/* Chart section */}
      <div className="p-6">
        <div className="h-80">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={processedData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis 
                dataKey="formattedDate" 
                stroke="#6b7280"
                fontSize={12}
                tick={{ fill: '#6b7280' }}
              />
              <YAxis 
                stroke="#6b7280"
                fontSize={12}
                tick={{ fill: '#6b7280' }}
                allowDecimals={false}
              />
              <Tooltip content={<CustomTooltip />} />
              <Bar 
                dataKey="registrations" 
                fill="#4f46e5" 
                radius={[4, 4, 0, 0]}
                stroke="#3730a3"
                strokeWidth={1}
              />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Alternative line chart view */}
        <div className="mt-8 h-64">
          <div className="mb-4">
            <h4 className="text-md font-medium text-gray-900">Registration Trend</h4>
            <p className="text-sm text-gray-500">Line view showing registration patterns over time</p>
          </div>
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={processedData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis 
                dataKey="formattedDate" 
                stroke="#6b7280"
                fontSize={12}
                tick={{ fill: '#6b7280' }}
              />
              <YAxis 
                stroke="#6b7280"
                fontSize={12}
                tick={{ fill: '#6b7280' }}
                allowDecimals={false}
              />
              <Tooltip content={<CustomTooltip />} />
              <Line 
                type="monotone" 
                dataKey="registrations" 
                stroke="#4f46e5" 
                strokeWidth={3}
                dot={{ fill: '#4f46e5', strokeWidth: 2, r: 4 }}
                activeDot={{ r: 6, stroke: '#4f46e5', strokeWidth: 2, fill: '#ffffff' }}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
};

export default UserRegistrationChart;